import { useMutationState } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sendKey, type PayrollEditorQueries } from "@/payroll/editor/query";
import { errorStatus } from "@/payroll/editor/state";
import type { EnrichedMessagePage, SendMessage } from "@/payroll/editor/types";

export interface Outgoing {
  id: string;
  message: string;
  committed: boolean;
  /** Persist recovery across a reload, including saved requests returning 503. */
  failed?: boolean;
  status?: number;
}

export type PostedOutgoing = Outgoing & {
  sending: boolean;
  failed: boolean;
  saved: boolean;
};

const read = (key: string): Outgoing[] => {
  try {
    const items: unknown = JSON.parse(sessionStorage.getItem(key) || "[]");
    return Array.isArray(items)
      ? items.filter(
          (item): item is Outgoing =>
            typeof item?.id === "string" &&
            typeof item?.message === "string" &&
            typeof item?.committed === "boolean",
        )
      : [];
  } catch {
    return [];
  }
};

/** Local intent survives reloads. Mutation state owns each HTTP request;
 * server polling owns readiness for the next background turn. */
export const useOutgoing = (
  storageKey: string,
  mutation: PayrollEditorQueries["send"],
  page: EnrichedMessagePage | undefined,
  businessId: string,
  workspaceId: string | undefined,
  allowed = true,
) => {
  const [items, setItems] = useState(() => read(storageKey));
  const records = useRef(items);
  const inFlight = useRef(new Set<string>());
  const write = useCallback(
    (next: Outgoing[]) => {
      records.current = next;
      setItems(next);
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // Retain the in-memory queue when browser storage is unavailable.
      }
    },
    [storageKey],
  );
  const arrived = useMemo(
    () =>
      new Set(
        page?.messages.flatMap((message) =>
          message.client_message_id ? [message.client_message_id] : [],
        ),
      ),
    [page],
  );
  const states = useMutationState({
    filters: { mutationKey: sendKey(businessId, workspaceId) },
    select: (entry) => ({
      id: (entry.state.variables as SendMessage | undefined)?.client_message_id,
      status: entry.state.status,
    }),
  });
  const latest = useCallback(
    (id: string) => states.filter((entry) => entry.id === id).at(-1),
    [states],
  );
  const ready = allowed && !!page && !page.isActive;
  const post = useCallback(
    (item: Outgoing) => {
      if (inFlight.current.has(item.id)) return;
      inFlight.current.add(item.id);
      write(
        records.current.map((entry) =>
          entry.id === item.id
            ? { ...entry, committed: true, failed: false, status: undefined }
            : entry,
        ),
      );
      mutation
        .mutateAsync({ message: item.message, client_message_id: item.id })
        .catch((error: unknown) => {
          // Arrival does not mean enqueue succeeded. Keep recovery until the
          // actual POST succeeds, and never recursively retry a conflict.
          write(
            records.current.map((entry) =>
              entry.id === item.id
                ? { ...entry, failed: true, status: errorStatus(error) }
                : entry,
            ),
          );
        })
        .finally(() => inFlight.current.delete(item.id));
    },
    [mutation, write],
  );

  // Send one queued item only after the server finishes its active turn.
  // A successful POST not yet seen by polling also blocks the next item.
  useEffect(() => {
    if (
      !ready ||
      inFlight.current.size ||
      states.some((entry) => entry.status === "pending")
    )
      return;
    if (
      items.some(
        (item) =>
          latest(item.id)?.status === "success" && !arrived.has(item.id),
      )
    )
      return;
    // A failure holds the line. Skipping it and sending the next one put the
    // owner's changes in a different order from the one they wrote them in,
    // which for payroll rules is a different answer. It moves again when they
    // retry it or take it out.
    if (items.some((item) => item.failed)) return;
    const next = items.find(
      (item) => !arrived.has(item.id) && !latest(item.id),
    );
    if (next) post(next);
  }, [ready, items, states, arrived, latest, post]);

  useEffect(() => {
    const remaining = items.filter(
      (item) =>
        !arrived.has(item.id) ||
        item.failed ||
        inFlight.current.has(item.id) ||
        latest(item.id)?.status === "pending" ||
        latest(item.id)?.status === "error",
    );
    if (remaining.length !== items.length) write(remaining);
  }, [items, arrived, latest, write]);

  const queued = items.filter((item) => !item.committed);
  const posted: PostedOutgoing[] = items.flatMap((item) => {
    if (!item.committed) return [];
    const status = latest(item.id)?.status;
    const sending = status === "pending";
    const failed = !sending && (item.failed === true || status === "error");
    const saved = arrived.has(item.id);
    if (saved && !failed) return [];
    return [{ ...item, sending, failed, saved }];
  });
  const sending = states.find((entry) => entry.status === "pending");
  const blocked = () => records.current.some((item) => item.failed);
  const add = (message: string, immediate: boolean) => {
    // The immediate path waits behind a failure too. Sending here while an
    // earlier request sat failed would be the same reordering by another
    // route, so it joins the queue instead and drains in turn.
    const send = immediate && ready && !blocked();
    const item = { id: crypto.randomUUID(), message, committed: send };
    write([...records.current, item]);
    if (send) post(item);
  };
  const remove = (id: string) =>
    write(records.current.filter((item) => item.id !== id));
  /** Rewrite a message that has not gone yet, in place. */
  const amend = (id: string, message: string) =>
    write(
      records.current.map((item) =>
        item.id === id ? { ...item, message } : item,
      ),
    );
  const promote = (id: string) => {
    const item = records.current.find((entry) => entry.id === id);
    if (!item) return;
    write([
      { ...item, committed: true },
      ...records.current.filter((entry) => entry.id !== id),
    ]);
  };
  const retry = (id: string) => {
    const item = records.current.find((entry) => entry.id === id);
    if (!item || !allowed) return;
    // A saved request can restart its own pending turn with the same id.
    if (ready || arrived.has(id) || item.status === 503) post(item);
  };
  return { queued, posted, sending, add, remove, amend, promote, retry };
};
export type OutgoingQueue = ReturnType<typeof useOutgoing>;
